// In-line duct fans (category 'fan', the catalogue's 95 Plumbing group;
// docs/claude/products.md, *Duct fans*): their plan symbol, drawn from the
// manufacturer's dimension drawings, and the figures of each model. The products
// themselves are Baserow rows; `DUCT_FAN_PRODUCTS` is the seed they were written
// from.

import type {
  ConnPort,
  GeomEntity,
  Geometry,
  ProductTemplate,
  Vec2,
} from './types';

/** A box on a duct fan — its terminal box, a top piece, a mounting rail — in mm:
 *  along the axis from the fan's middle (`x`), across from the axis toward local
 *  +y (`y`) and up from the axis (`z`). `terminal`: its power comes in there. */
export interface FanBox {
  x: readonly [number, number];
  y: readonly [number, number];
  z: readonly [number, number];
  terminal?: boolean;
}

/** A duct fan's figures (mm), from its datasheet's drawings: ONE description its
 *  plan symbol (its top view) and its 3D model are both made from. */
export interface DuctFanShape {
  /** Overall length, spigot end to spigot end (local x). */
  length: number;
  /** Overall width across it, its boxes included (local y). */
  width: number;
  /** Overall height, its base to its top. */
  height: number;
  /** The duct axis: this far off the footprint's middle across it, this high
   *  above the fan's base. */
  axis: { y: number; z: number };
  /** Its outline revolved about the axis: `[x, Ø]` stations from its middle out
   *  to an end, each end alike. A repeated x is a step (a spigot off a collar). */
  profile: ReadonlyArray<readonly [number, number]>;
  boxes: readonly FanBox[];
}

// Fills carry their own alpha (the plan shades a plain one); the outlines take
// the part's own colour, as any product drawing's do.
const BODY = '#c9d1d866';
const BOX = '#c9d1d8b3';
const ARROW = '#3d7fd1';

/** The body's radius at `x` along it (the larger at a step). */
export function fanRadiusAt(profile: DuctFanShape['profile'], x: number): number {
  const ax = Math.abs(x);
  let r = 0;
  for (let i = 0; i < profile.length; i++) {
    const [x0, d0] = profile[i], next = profile[i + 1];
    if (ax === x0) r = Math.max(r, d0 / 2);
    if (next && ax > x0 && ax < next[0]) r = Math.max(r, (d0 + ((ax - x0) / (next[0] - x0)) * (next[1] - d0)) / 2);
  }
  return r;
}

/** The body's largest radius over a run of it. */
function radiusOver(profile: DuctFanShape['profile'], [a, b]: readonly [number, number]): number {
  let r = Math.max(fanRadiusAt(profile, a), fanRadiusAt(profile, b));
  for (const [x] of profile) for (const s of [x, -x]) if (s > a && s < b) r = Math.max(r, fanRadiusAt(profile, s));
  return r;
}

/** A duct fan's plan symbol, seen from above: its outline revolved from its
 *  profile, a line at each joint, each box — whole where it rises above the
 *  body, else where it stands out past it — and an arrow the way the air flows
 *  (+x). Bounds are its footprint, centred on the origin. */
export function ductFanGeometry(f: DuctFanShape): Geometry {
  const ay = f.axis.y;
  const at = (side: 1 | -1) => f.profile.map(([x, d]) => ({ x, y: ay + side * d / 2 }));
  const flip = (pts: Array<{ x: number; y: number }>) => pts.map((p) => ({ x: -p.x, y: p.y }));
  const top = at(1), bottom = at(-1);
  // Its top edge from the −x end to the +x end, then its bottom edge back.
  const ring = [...flip(top).reverse(), ...top.slice(1), ...bottom.slice().reverse(), ...flip(bottom.slice(1))];
  const entities: GeomEntity[] = [
    { kind: 'hatch', loops: [ring], solid: true, color: BODY },
    { kind: 'polyline', points: ring, closed: true },
  ];
  const crown = Math.max(...f.profile.map(([, d]) => d / 2));
  for (const b of f.boxes) {
    let [y0, y1] = b.y;
    if (b.z[1] <= crown) {
      const r = radiusOver(f.profile, b.x);
      if (y0 >= 0) y0 = Math.max(y0, r);
      else if (y1 <= 0) y1 = Math.min(y1, -r);
      else continue;
      if (y1 <= y0) continue;
    }
    const rect = [{ x: b.x[0], y: ay + y0 }, { x: b.x[1], y: ay + y0 }, { x: b.x[1], y: ay + y1 }, { x: b.x[0], y: ay + y1 }];
    entities.push({ kind: 'hatch', loops: [rect], solid: true, color: BOX }, { kind: 'polyline', points: rect, closed: true });
  }
  // A joint where the outline turns (a taper, a collar, a spigot), both ends.
  for (let i = 1; i < f.profile.length - 1; i++) {
    const [x] = f.profile[i];
    if (f.profile[i - 1][0] === x) continue; // a step's second station: its line is drawn
    const d = Math.max(f.profile[i][1], f.profile[i + 1][0] === x ? f.profile[i + 1][1] : 0);
    for (const s of [1, -1]) entities.push({ kind: 'line', a: { x: s * x, y: ay - d / 2 }, b: { x: s * x, y: ay + d / 2 } });
  }
  const a = 0.6 * (f.profile.find(([x]) => x > 0)?.[0] ?? f.length / 4), h = 0.35 * a;
  entities.push(
    { kind: 'line', a: { x: -a, y: ay }, b: { x: a, y: ay }, color: ARROW },
    { kind: 'polyline', points: [{ x: a - h, y: ay + h * 0.6 }, { x: a, y: ay }, { x: a - h, y: ay - h * 0.6 }], closed: false, color: ARROW },
  );
  return { entities, bounds: { min: { x: -f.length / 2, y: -f.width / 2 }, max: { x: f.length / 2, y: f.width / 2 } } };
}

/** A duct fan's connection points: the duct in at its −x end and out at its +x
 *  end, on the duct axis, and its power at the outer face of its terminal box. */
export function ductFanPorts(f: DuctFanShape): ConnPort[] {
  const ny = f.axis.y / (f.width / 2);
  const ports: ConnPort[] = [
    { id: 'inlet', name: 'Inlet', nx: -1, ny, type: 'plumbing' },
    { id: 'outlet', name: 'Outlet', nx: 1, ny, type: 'plumbing' },
  ];
  const t = f.boxes.find((b) => b.terminal);
  if (t) {
    const outer = Math.abs(t.y[0]) > Math.abs(t.y[1]) ? t.y[0] : t.y[1];
    ports.push({ id: 'power', name: 'Supply', nx: (t.x[0] + t.x[1]) / f.length, ny: (f.axis.y + outer) / (f.width / 2), type: 'electrical' });
  }
  return ports;
}

/** A duct fan in 3D, in its local frame above its base (mm): its body revolved
 *  about the duct axis — stations end to end, `[x, radius]`, a repeated x a step —
 *  and its boxes as prisms. */
export function ductFanSolids(f: DuctFanShape): { axis: { y: number; z: number }; stations: Array<[number, number]>; boxes: Array<{ ring: Vec2[]; z0: number; z1: number }> } {
  // The profile starts at the middle: mirrored, its first station once.
  const half = f.profile.map(([x, d]): [number, number] => [x, d / 2]);
  const ay = f.axis.y, az = f.axis.z;
  return {
    axis: { ...f.axis },
    stations: [...half.slice(1).reverse().map(([x, r]): [number, number] => [-x, r]), ...half],
    boxes: f.boxes.map((b) => ({
      ring: [{ x: b.x[0], y: ay + b.y[0] }, { x: b.x[1], y: ay + b.y[0] }, { x: b.x[1], y: ay + b.y[1] }, { x: b.x[0], y: ay + b.y[1] }],
      z0: az + b.z[0], z1: az + b.z[1],
    })),
  };
}

/** Soler & Palau TD-160/100 N SILENT (datasheet TD-SILENT, p. 709): a Ø135.5 body
 *  between 28 mm Ø97 spigots, 232 long; its terminal box on one side, 82 from
 *  the axis and 95 tall; a top piece to 95.5 above the axis (163 overall). */
const TD_160: DuctFanShape = {
  length: 232, width: 151, height: 163, axis: { y: -6.5, z: 67.75 },
  profile: [[0, 135.5], [60, 135.5], [88, 97], [116, 97]],
  boxes: [
    { x: [-47.5, 47.5], y: [24, 82], z: [-47.5, 47.5], terminal: true },
    { x: [-43.5, 43.5], y: [-24, 24], z: [55, 95.25] },
  ],
};

/** Soler & Palau TD-250/100 SILENT (p. 709): A 575, a barrel body ØD 204 behind
 *  short Ø123 collars and Ø97 spigots; its terminal box C − H = 131 out on one
 *  side, its mounting rail H = 121 out and E = 100 tall on the other (its holes
 *  F = 250 apart). The body's and the boxes' lengths are read off the drawing's
 *  proportions. */
const TD_250: DuctFanShape = {
  length: 575, width: 252, height: 204, axis: { y: 5, z: 102 },
  profile: [[0, 204], [130, 198], [209, 176], [209, 123], [241, 123], [241, 97], [287.5, 97]],
  boxes: [
    { x: [-60, 60], y: [-131, -90], z: [-55, 55], terminal: true },
    { x: [-145, 145], y: [88, 121], z: [-50, 50] },
  ],
};

/** A fan's figures, by its product's `fanModel`. */
export function ductFanShapeOf(model: string | undefined): DuctFanShape | null {
  return (model && SHAPES[model]) || null;
}

const fan = (id: string, model: string, name: string, it: string, fr: string, shape: DuctFanShape, description: string, properties: ProductTemplate['properties']): ProductTemplate => ({
  id, name, nameI18n: { en: name, it, fr }, category: 'fan', fanModel: model,
  width: shape.length, depth: shape.width, height: shape.height, color: '#c9d1d8',
  description, builtin: false, lockResize: true,
  geometry: ductFanGeometry(shape), ports: ductFanPorts(shape), properties,
});

/** The duct fans: two speeds, 230 V 50 Hz, IP44, for Ø100 ducts. */
export const DUCT_FAN_PRODUCTS: ProductTemplate[] = [
  fan('builtin-fan-td160-100', 'td160-100', 'TD-160-100 Silent Fan', 'Ventilatore TD-160-100 Silent', 'Ventilateur TD-160-100 Silent', TD_160,
    'Ultra-quiet in-line mixed-flow duct fan (Soler & Palau TD-160/100 N SILENT), motor on silent-elastic blocks, for Ø100 ducts.', [
      { key: 'manufacturer', label: 'Manufacturer', value: 'Soler & Palau' },
      { key: 'model', label: 'Model', value: 'TD-160/100 N SILENT' },
      { key: 'duct', label: 'Duct diameter', value: 100, unit: 'mm' },
      { key: 'airflow', label: 'Max airflow (high / low)', value: '180 / 150', unit: 'm³/h' },
      { key: 'power', label: 'Power (high / low)', value: '29 / 18', unit: 'W' },
      { key: 'current', label: 'Current (high / low)', value: '0.17 / 0.11', unit: 'A' },
      { key: 'speed', label: 'Speed (high / low)', value: '2400 / 2200', unit: 'rpm' },
      { key: 'sound', label: 'Sound pressure at 3 m (high / low)', value: '24 / 22', unit: 'dB(A)' },
      { key: 'supply', label: 'Supply', value: '230 V 50 Hz, IP44' },
      { key: 'airTemp', label: 'Air temperature', value: '-20 / +40', unit: '°C' },
      { key: 'weight', label: 'Weight', value: 1.4, unit: 'kg' },
      { key: 'controls', label: 'Controls', value: 'COM-2, REGUL-2; RMB-1,5, REB-1' },
    ]),
  fan('builtin-fan-td250-100', 'td250-100', 'TD-250-100 Silent Fan', 'Ventilatore TD-250-100 Silent', 'Ventilateur TD-250-100 Silent', TD_250,
    'Ultra-quiet in-line mixed-flow duct fan (Soler & Palau TD-250/100 SILENT), sound-absorbent insulated body, for Ø100 ducts.', [
      { key: 'manufacturer', label: 'Manufacturer', value: 'Soler & Palau' },
      { key: 'model', label: 'Model', value: 'TD-250/100 SILENT' },
      { key: 'duct', label: 'Duct diameter', value: 100, unit: 'mm' },
      { key: 'airflow', label: 'Max airflow (high / low)', value: '250 / 200', unit: 'm³/h' },
      { key: 'power', label: 'Power (high / low)', value: '27 / 21', unit: 'W' },
      { key: 'current', label: 'Current (high / low)', value: '0.12 / 0.10', unit: 'A' },
      { key: 'speed', label: 'Speed (high / low)', value: '2210 / 1680', unit: 'rpm' },
      { key: 'sound', label: 'Sound pressure at 3 m (high / low)', value: '25 / 20', unit: 'dB(A)' },
      { key: 'supply', label: 'Supply', value: '230 V 50 Hz, IP44' },
      { key: 'airTemp', label: 'Air temperature', value: '-20 / +40', unit: '°C' },
      { key: 'weight', label: 'Weight', value: 5.4, unit: 'kg' },
      { key: 'controls', label: 'Controls', value: 'COM-2, REGUL-2; RMB-1,5, REB-1' },
    ]),
];

const SHAPES: Readonly<Record<string, DuctFanShape>> = {
  'td160-100': TD_160,
  'td250-100': TD_250,
};

/** The fans there are figures for: what a product's `fanModel` may name. */
export const DUCT_FAN_MODELS: readonly string[] = Object.keys(SHAPES);
